import { ArrowBack } from '@mui/icons-material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { Box, IconButton, Typography, useTheme } from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button as ShadcnButton } from '@/components/ui/button';
import useCustomPosthogEvent from '@/modules/common/application/use-custom-posthog-event';
import { Loader } from '@/modules/common/components';
import Button from '@/modules/common/components/Button/Button';
import { ConversationType, MessageStatus, SenderType, ToolType } from '@/modules/common/types';
import { CustomPosthogEvent } from '@/modules/common/types/customPosthogEvent';

import { useConversation } from '../application/use-conversation';
import { useMessage } from '../application/use-message';
import { SendMessageConversation } from '../types/conversation';
import { ChatMessage } from './ChatMessage';
import { MessageInputField } from './MessageInputField';

interface ChatWindowProps {
  conversation: SendMessageConversation | null;
  setConversation: (conversation: SendMessageConversation | null) => void;
  onBack: () => void;
}

export function ChatWindow({ conversation, setConversation, onBack }: ChatWindowProps) {
  const { t } = useTranslation();
  const theme = useTheme();
  const [newMessage, setNewMessage] = useState('');
  const { trackEvent } = useCustomPosthogEvent();
  const {
    markConversationAsCompleted,
    markConversationAsCompletedLoading,
    getConversationById,
    startConversation,
    startConversationLoading,
  } = useConversation();
  const { messages, sendMessage, sendMessageLoading } = useMessage({
    conversation,
    setConversation,
  });
  const [toolType, setToolType] = useState<ToolType>(ToolType.NORMAL);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const updatedConversation = getConversationById(conversation?.id ?? '');
  const conversationId = updatedConversation?.id;
  const isMarkedCompleted = updatedConversation?.isCompleted;
  const lastMessage = messages[messages.length - 1];

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  function getRequestType(toolType: ToolType) {
    return toolType === ToolType.DEEP_SEARCH ? 'deep-search' : 'question';
  }

  const handleSendMessage = () => {
    sendMessage({ content: newMessage, tool: toolType });
    setNewMessage('');
    trackEvent(CustomPosthogEvent.CHAT_ACTION, {
      action: 'send-request',
      requestType: getRequestType(toolType),
      conversationId: updatedConversation?.id,
    });
  };

  const handleStartConversation = async (type: ConversationType) => {
    const newConversation = await startConversation({
      variables: { type },
    });
    if (type === ConversationType.DEEP_SEARCH) {
      setToolType(ToolType.DEEP_SEARCH);
    }
    if (newConversation.data?.startConversation) {
      setConversation(newConversation.data.startConversation);
      trackEvent(CustomPosthogEvent.CHAT_ACTION, {
        action: 'start-conversation',
        requestType: getRequestType(toolType),
        conversationId: newConversation.data.startConversation.id,
      });
    }
  };

  const handleMarkConversationCompleted = () => {
    if (conversation?.id) {
      markConversationAsCompleted({
        variables: { conversationId: conversation.id },
      });
      trackEvent(CustomPosthogEvent.CHAT_ACTION, {
        action: 'complete-conversation',
        requestType: getRequestType(toolType),
        conversationId: updatedConversation?.id,
      });
    }
  };

  return (
    <Box display="flex" flexDirection="column" height="100%">
      <Box p={1.5} borderBottom={1} borderColor="divider">
        <IconButton onClick={onBack}>
          <ArrowBack color="primary" fontSize="small" />
        </IconButton>
      </Box>
      {/* Messages */}
      <Box
        sx={{
          flexGrow: 1,
          overflowY: 'auto',
          p: 2,
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
        }}
      >
        {conversationId ? (
          <>
            {messages.map((message, index) => {
              const isLastMessage = index === messages.length - 1;
              return (
                <Box key={message.id} sx={{ mb: isLastMessage ? -2 : undefined }}>
                  <ChatMessage message={message} />
                </Box>
              );
            })}
            <div ref={messagesEndRef} />
          </>
        ) : startConversationLoading ? (
          <Box display="flex" justifyContent="center" alignItems="center" height="100%">
            <Loader />
          </Box>
        ) : (
          <>
            <Box display="flex" flexDirection="column" gap={1}>
              <Typography variant="body2" color="text.secondary">
                {t('modules.chat.chatWindow.chooseRequestType')}
              </Typography>
              <ShadcnButton
                variant="ghost"
                className="w-fit bg-primary/10 hover:bg-primary/20 shadow-none py-1 px-2.5 h-8"
                onClick={() => {
                  handleStartConversation(ConversationType.QUESTION);
                }}
              >
                <span className="text-primary text-sm">
                  {t('modules.chat.chatWindow.askAQuestion')}
                </span>
              </ShadcnButton>
              <ShadcnButton
                variant="ghost"
                className="w-fit bg-primary/10 hover:bg-primary/20 shadow-none py-1 px-2.5 h-8"
                onClick={() => {
                  handleStartConversation(ConversationType.DEEP_SEARCH);
                }}
              >
                <span className="text-primary text-sm">
                  {t('modules.chat.chatWindow.performDeepSearch')}
                </span>
              </ShadcnButton>
            </Box>
          </>
        )}
        {lastMessage?.sender === SenderType.BOT &&
          lastMessage.status === MessageStatus.COMPLETED && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1 }}>
              <Button
                variant={isMarkedCompleted ? 'contained' : 'outlined'}
                color="success"
                size="small"
                onClick={handleMarkConversationCompleted}
                loading={markConversationAsCompletedLoading}
                startIcon={
                  isMarkedCompleted ? (
                    <CheckCircleOutlineIcon style={{ color: 'white' }} />
                  ) : undefined
                }
                buttonLabelStyles={{
                  color: isMarkedCompleted
                    ? 'white !important'
                    : theme.palette.success.main + ' !important',
                }}
                sx={{
                  color: isMarkedCompleted ? 'white' : theme.palette.success.main,
                  backgroundColor: isMarkedCompleted ? theme.palette.success.main : 'transparent',
                  '&.Mui-disabled': {
                    backgroundColor: isMarkedCompleted ? theme.palette.success.main : 'transparent',
                    color: isMarkedCompleted ? 'white' : theme.palette.success.main,
                    border: isMarkedCompleted ? 'none' : `1px solid ${theme.palette.success.main}`,
                  },
                  '&:hover': {
                    backgroundColor: isMarkedCompleted
                      ? theme.palette.success.main
                      : theme.palette.success.light,
                  },
                }}
                disabled={isMarkedCompleted}
              >
                {t(
                  `modules.chat.chatWindow.completeConversation.${
                    isMarkedCompleted ? 'completed' : 'uncompleted'
                  }`
                )}
              </Button>
            </Box>
          )}
      </Box>
      <Box sx={{ p: 2 }}>
        <MessageInputField
          toolType={toolType}
          setToolType={setToolType}
          disabledSendButton={Boolean(
            sendMessageLoading || lastMessage?.status === MessageStatus.IN_PROGRESS
          )}
          disabledToolType={!lastMessage}
          sendMessage={handleSendMessage}
          inputMessage={newMessage}
          setInputMessage={setNewMessage}
        />
      </Box>
    </Box>
  );
}
